import React, { Fragment, useState } from 'react';
import axios from 'axios';
import { isEmpty } from 'lodash';
import Router from 'next/router';
import { Flex, Text, Button, Image, Icon, useColorMode } from '@chakra-ui/react';
import { Menu, MenuButton, MenuList, MenuItem, Avatar } from '@chakra-ui/react';
import { FiSettings, FiUser, FiPlusCircle, FiGrid, FiChevronDown } from 'react-icons/fi';
import { RiLogoutCircleRLine, RiCheckboxCircleFill } from 'react-icons/ri';
import notification from 'components/Notifications';
import { API } from 'config/api';
import { decryptECIES, signECDSA } from '@stacks/encryption';
import useUser from 'lib/useUser';
import { authenticate, userSession, validateAuth } from 'utils/stacks';
import { useHttp } from 'lib/useHttp';
import { getStxAddress } from 'lib/utility';
import { bgColor, borderColor, colorText } from 'config/data';
import ModeSwitcher from 'components/ModeSwitcher';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useCurrency } from 'contexts/currency/currency.provider';

const AuthMenu = ({ isAuthenticated, onOpen, onLogout, user, access_token, openOAuth }) => {
  const [cargando, setCargando] = useState(false);
  const { mutateUser } = useUser();
  const { colorMode } = useColorMode();
  const { data: wallets } = useHttp(`${process.env.NEXT_PUBLIC_API_URL}/me/wallets`, access_token);
  const { currency, changeCurrency } = useCurrency();

  const changeHandler = (val) => {
    changeCurrency(val);
  };

  const onLoginWallet = async () => {
    setCargando(true);
    authenticate(async () => {
      let config = { headers: { Authorization: `Bearer ${access_token}` } };
      let userData = userSession.loadUserData();
      const appsMeta = userData.profile.appsMeta;
      const address =
        process.env.NEXT_PUBLIC_ENV === 'production'
          ? userData.profile.stxAddress.mainnet
          : userData.profile.stxAddress.testnet;
      const private_key = userData.appPrivateKey;
      const appUrl = process.env.NEXT_PUBLIC_ENV === 'dev' ? 'http://localhost:3000' : process.env.NEXT_PUBLIC_URL;
      const public_key = appsMeta[appUrl].publicKey;

      API.post('/auth/login/wallet/initiate', { address, public_key }, config)
        .then(async (res) => {
          const challenge_token = res.data.challenge_token;
          const method = res.data.method;
          if (method === 'sign_challenge_token') {
            const deciphered = await decryptECIES(private_key, challenge_token);
            const signature = await signECDSA(private_key, deciphered);
            const dataComplete = {
              challenge_token: deciphered,
              signature: signature.signature,
              public_key,
              address,
              method,
            };
            API.post('/auth/login/wallet/complete', dataComplete, config)
              .then(async (resAuth) => {
                try {
                  mutateUser(await axios.post('/api/login_wallet', resAuth.data.data)).then(() => {
                    setCargando(false);
                    // onClose();
                    notification('error', '✅ Updated profile');
                  });
                } catch (error) {
                  setCargando(false);
                  notification('error', error.response.data.message);
                }
              })
              .catch((errAuth) => {
                setCargando(false);
                notification('error', errAuth.response.data.message);
              });
          } else {
            const p = new Promise((resolve) => validateAuth(private_key, challenge_token, resolve));
            p.then(async (stacksTransaction) => {
              const transaction = stacksTransaction.serialize().toString('hex');
              const deciphered = await decryptECIES(private_key, challenge_token);
              const signature = await signECDSA(private_key, transaction);
              const dataComplete = {
                public_key,
                address,
                signature: signature.signature,
                transaction,
                challenge_token: deciphered,
                method,
              };
              API.post('/auth/login/wallet/complete', dataComplete, config)
                .then(async (resAuth) => {
                  try {
                    mutateUser(await axios.post('/api/login_wallet', resAuth.data.data)).then(() => {
                      setCargando(false);
                      // onClose();
                      notification('error', '✅ Updated profile');
                    });
                  } catch (error) {
                    setCargando(false);
                    notification('error', error.response.data.message);
                  }
                })
                .catch((errAuth) => {
                  setCargando(false);
                  notification('error', errAuth.response.data.message);
                });
            }).catch((e) => setCargando(false));
          }
        })
        .catch((errIni) => {
          setCargando(false);
          notification('error', errIni.response.data.message);
        });
    });
  };

  return !isAuthenticated ? (
    <Fragment>
      <Flex
        mb={[5, 0]}
        mr="0px"
        alignItems="center"
        borderWidth={2}
        bg={colorMode === 'dark' ? '#fff' : null}
        borderColor={colorMode === 'dark' ? null : '#000'}
        p="9px 16px"
        borderRadius={22}
        // onClick={onOpen}
        onClick={() => openOAuth()}
      >
        <Flex cursor="pointer">
          <Image src="/images/icon-plus_light.svg" alt="" w="20px" mr="10px" />
          <Text color="black" fontSize="14px" fontWeight="bold">
            Create
          </Text>
        </Flex>
      </Flex>
      <Button ml="15px" px="18px" variant="black" onClick={() => openOAuth()} mr="15px">
        Sign Up / Sign In
      </Button>

      <ModeSwitcher />
      <Menu>
        <MenuButton fontSize={13} fontWeight="bold" mt={{ base: '20px', md: 0 }}>
          <Flex ml="12px" alignItems="center">
            <Flex alignItems="center">
              <Text color={colorText[colorMode]} textTransform="uppercase">
                {currency}
              </Text>
              <Icon as={FiChevronDown} ml="10px" />
            </Flex>
          </Flex>
        </MenuButton>
        <MenuList minWidth="120px" bg={bgColor[colorMode]} borderColor={borderColor[colorMode]}>
          <MenuItem onClick={() => changeHandler('stx')}>STX</MenuItem>
          <MenuItem onClick={() => changeHandler('usd')}>USD</MenuItem>
        </MenuList>
      </Menu>
    </Fragment>
  ) : (
    <Fragment>
      <Menu>
        <MenuButton fontSize={13} fontWeight="bold">
          <Flex
            id="create"
            mr={{ base: '10px', md: '20px' }}
            alignItems="center"
            borderWidth={2}
            bg={colorMode === 'dark' ? '#fff' : null}
            borderColor={colorMode === 'dark' ? null : '#000'}
            p="9px 15px"
            borderRadius={22}
          >
            <Flex cursor="pointer">
              <Image src="/images/icon-plus_light.svg" alt="" w="20px" mr="8px" />
              <Text color="black" fontSize="14px" fontWeight="bold">
                Create
              </Text>
            </Flex>
          </Flex>
        </MenuButton>

        <MenuList
          fontSize="14px"
          minWidth="210px"
          p={0}
          mt="20px"
          ml="-25px"
          bg={bgColor[colorMode]}
          borderColor={colorMode === 'dark' ? '#333' : null}
        >
          <MenuItem
            icon={<FiPlusCircle fontSize="18px" />}
            borderTopWidth={1}
            borderBottomWidth={1}
            borderColor={colorMode === 'dark' ? '#333' : null}
            py="10px"
            fontWeight="500"
            onClick={() => Router.push('/create/single')}
          >
            Create NFT
          </MenuItem>
          <MenuItem
            onClick={() => Router.push('/create/collection')}
            icon={<FiGrid fontSize="17px" />}
            borderBottomWidth={1}
            borderColor={colorMode === 'dark' ? '#333' : null}
            py="10px"
            fontWeight="500"
          >
            Create Collection
          </MenuItem>
        </MenuList>
      </Menu>

      <Menu>
        <MenuButton fontSize={13} fontWeight="bold">
          <Flex mr="5px" alignItems="center">
            <Flex pos="relative">
              <Avatar w="36px" h="36px" name={user?.username} mr="10px" size="sm" bg="#BC3153" color="#fff" />
              {isEmpty(wallets?.data) && (
                <Image
                  pos="absolute"
                  bottom={0}
                  right={3}
                  w="16px"
                  h="15px"
                  src="/images/icon-warning.svg"
                  alt="warning"
                />
              )}
            </Flex>
            <Flex flexDirection="column" textAlign="left">
              <Text color={colorMode === 'dark' ? 'gray.300' : 'black'}>{user?.username || ''}</Text>
            </Flex>
          </Flex>
        </MenuButton>
        <MenuList
          fontSize="14px"
          minWidth="270px"
          p={0}
          mt="20px"
          ml="-25px"
          bg={bgColor[colorMode]}
          borderColor={colorMode === 'dark' ? '#333' : null}
        >
          <Flex p="20px 15px 3px">
            <Image w="30px" h="30px" src="/images/hiro-wallet.svg" alt="Hiro waller" mr="8px" />
            <Flex flexDirection="column">
              <Text fontSize="10px">{isEmpty(wallets?.data) ? 'No information' : 'Account 01'}</Text>
              <Text fontSize="14px" fontWeight="semibold">
                {isEmpty(wallets?.data) ? 'Hiro Wallet' : getStxAddress(wallets?.data[0]?.address)}
              </Text>
            </Flex>
          </Flex>
          {isEmpty(wallets?.data) ? (
            <Flex justifyContent="space-between" p="0px 15px 10px" alignItems="center">
              <Flex>
                <Image src="/images/akar-icons-circle-check-fill.svg" alt="" w="16px" h="16px" mr="10px" />
                <Text fontSize="12px">Wallet not synced</Text>
              </Flex>
              <Button variant="black" fontSize="12px" size="sm" onClick={onLoginWallet}>
                Connect
              </Button>
            </Flex>
          ) : (
            <Flex justifyContent="space-between" p="0px 15px 10px" alignItems="center">
              <Flex alignItems="center">
                <Icon as={RiCheckboxCircleFill} color="#00c783" mr="10px" />
                <Text fontSize="12px">Wallet synced</Text>
              </Flex>
            </Flex>
          )}

          <MenuItem
            icon={<FiUser fontSize="18px" />}
            borderTopWidth={1}
            borderBottomWidth={1}
            borderColor={colorMode === 'dark' ? '#333' : null}
            py="10px"
            fontWeight="500"
            onClick={() => Router.push('/favorites')}
          >
            My Profile
          </MenuItem>
          <MenuItem
            onClick={() => Router.push('/settings')}
            icon={<FiSettings fontSize="17px" />}
            borderBottomWidth={1}
            borderColor={colorMode === 'dark' ? '#333' : null}
            py="10px"
            fontWeight="500"
          >
            Settings
          </MenuItem>
          {/* <MenuItem
          icon={<FiHeart fontSize="18px" />}
          borderBottomWidth={1}
          py="10px"
          fontWeight="500"
          onClick={() => Router.push('/favorites')}
        >
          Favorites
        </MenuItem> */}
          {/* <MenuItem
          onClick={() => Router.push('/my-collection')}
          icon={<BiCollection fontSize="17px" />}
          borderBottomWidth={1}
          py="10px"
          fontWeight="500"
        >
          My Collections
        </MenuItem> */}
          <MenuItem icon={<RiLogoutCircleRLine fontSize="17px" />} py="10px" fontWeight="500" onClick={onLogout}>
            Sign Out
          </MenuItem>
        </MenuList>
      </Menu>
      <Flex ml="15px">
        <ModeSwitcher />
      </Flex>
      <Menu>
        <MenuButton fontSize={13} fontWeight="bold">
          <Flex ml="12px" alignItems="center">
            <Flex alignItems="center">
              <Text textTransform="uppercase">{currency}</Text>
              <Icon as={FiChevronDown} ml="10px" />
            </Flex>
          </Flex>
        </MenuButton>
        <MenuList minWidth="120px" bg={bgColor[colorMode]} borderColor={borderColor[colorMode]}>
          <MenuItem onClick={() => changeHandler('usd')}>USD</MenuItem>
          <MenuItem onClick={() => changeHandler('stx')}>STX</MenuItem>
        </MenuList>
      </Menu>
    </Fragment>
  );
};
export default AuthMenu;
